import React, { useState } from "react";
import NavigateBack from "./NavigateBack";
import { useStateContext } from "../contexts/ContextProvider";
import Button from "./Button";
import ProgressBar from "./ProgressBar";
import { DateTime } from "luxon";
import { apiCreateTransaction } from "../services/apiService";

const initialErrorState = {
  paymentType: "",
  paymentAmount: "",
  paymentDate: "",
};

const AcceptPaymentModal = (props) => {
  const { lease } = props;
  const { setTransactions, handleModal } = useStateContext();
  const [page, setPage] = useState(1);
  const [error, setError] = useState(initialErrorState);
  const [isLoading, setIsLoading] = useState(false);
  const pages = 4;

  const [transaction, setTransaction] = useState({
    leaseId: lease.id,
    landlordId: lease.landlordId,
    tenantId: lease.tenantId,
    propertyId: lease.propertyId,
    roomId: lease.roomId,
    type: "payment",
    name: "--",
    amount: "",
    dueDate: "",
    paymentStatus: "complete",
  });

  const nextPage = async () => {
    if (hasErrors()) {
      return;
    } else {
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    setPage(page - 1);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setTransaction((prevState) => ({ ...prevState, [name]: value }));
  };

  const addPayment = async () => {
    setIsLoading(true);
    const response = await apiCreateTransaction(transaction);
    if (response.hasError) {
      setIsLoading(false);
      console.log(response.error.response.statusText);
    } else {
      setTransactions((prevState) => [response.data, ...prevState]);
      handleModal("acceptPayment");
      setIsLoading(false);
    }
  };

  const hasErrors = () => {
    const reg = new RegExp("^[0-9]+(.[0-9]{2})?$"); // fixed to two decimal places?
    if (page === 1 && transaction.name === "--") {
      activateError("paymentType");
      return true;
    } else if (
      page === 2 &&
      (!transaction.amount || !reg.test(transaction.amount))
    ) {
      activateError("paymentAmount");
      return true;
    } else if (page === 3 && !transaction.dueDate) {
      activateError("paymentDate");
      return true;
    } else {
      return false;
    }
  };

  const activateError = (item) => {
    setError((prevState) => ({
      ...prevState,
      [item]: "animate-shake text-red-600 border-red-600",
    }));
    setTimeout(() => {
      setError((prevState) => ({
        ...prevState,
        [item]: "",
      }));
    }, 500);
    clearTimeout();
  };

  return (
    <div className="w-screen h-screen fixed top-0 left-0 right-0 bg-white mt-20 z-10">
      <ProgressBar pages={pages} currentPage={page} />
      <form
        className="grid justify-items-center"
        onSubmit={(e) => {
          e.preventDefault();
          page === pages ? addPayment() : nextPage();
        }}
      >
        {page === 1 && (
          <>
            <div
              className={`flex justify-center text-xl mt-10 ${error.paymentType}`}
            >
              What type of payment did you receive?
            </div>
            <label className="grid mt-5">
              <select
                id="name"
                name="name"
                value={transaction.name}
                className={`w-40 text-lg border-gray-300 rounded-lg border-1 p-2 ${error.paymentType}`}
                onChange={handleChange}
                onChangeCapture={() => console.log("selecting type")}
              >
                <option disabled>--</option>
                <option value="Offline - Cash">Cash</option>
                <option value="Offline - Check">Check</option>
                <option value="Offline - Money Order">Money Order</option>
                <option value="Offline - Zelle">Zelle</option>
                <option value="Offline - Cash App">Cash App</option>
                <option value="Offline - Chime">Chime</option>
                <option value="Offline - Other">Other</option>
              </select>
            </label>
          </>
        )}
        {page === 2 && (
          <>
            <div
              className={`flex justify-center text-xl mt-10 ${error.paymentAmount}`}
            >
              How much did you receive?
            </div>
            <label className="mt-5">
              <input
                id="amount"
                name="amount"
                type="number"
                maxLength={7}
                placeholder="$0.00"
                value={transaction.amount}
                className={`w-40 text-xl text-right border-gray-300 rounded-lg border-1 p-2 ${error.paymentAmount}`}
                onChange={handleChange}
              />
            </label>
          </>
        )}
        {page === 3 && (
          <>
            <div className="flex justify-center text-xl mt-10">
              When did you receive this payment?
            </div>
            <label className="mt-5">
              <input
                id="due-date"
                name="dueDate"
                type="date"
                // min={DateTime.now().minus({ months: 2 }).toFormat("yyyy-MM-dd")}
                max={DateTime.now().plus({ days: 1 }).toFormat("yyyy-MM-dd")}
                value={transaction.dueDate}
                autoFocus={true}
                className={`border-gray-300 border-1 rounded-md pl-1 ${error.paymentDate}`}
                onChange={handleChange}
              />
            </label>
          </>
        )}
        {page === pages && (
          <>
            <div className="flex justify-center text-xl mt-10">
              Payment Summary
            </div>
            <div
              className="grid grid-cols-2 gap-y-3 gap-x-5 items-center border-gray-300 border-2 
            rounded-2xl mt-10 p-5 capitalize"
            >
              <div>Type:</div>
              <div
                className="flex justify-center text-center text-blue-500 hover:text-blue-700 
                cursor-pointer"
                onClick={() => setPage(1)}
              >
                {transaction.name.split("- ")[1]}
              </div>
              <div>Received On:</div>
              <div
                className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                onClick={() => setPage(3)}
              >
                {DateTime.fromISO(transaction.dueDate).toFormat("MM/dd/yyyy")}
              </div>
              <div>Amount:</div>
              <div
                className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                onClick={() => setPage(2)}
              >
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 2,
                }).format(transaction.amount)}
              </div>
            </div>
          </>
        )}
      </form>
      <div className="flex justify-center w-full fixed bottom-28">
        <Button
          background="bg-slate-400"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === 1 ? "Cancel" : "Back"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={
            page === 1 ? () => handleModal("acceptPayment") : prevPage
          }
        />
        <Button
          isDisabled={isLoading}
          background="bg-snappily-orange"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === pages ? "Save" : "Next"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={page === pages ? addPayment : nextPage}
        />
      </div>
    </div>
  );
};

export default AcceptPaymentModal;
